import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, CheckCircle, Calendar, Users, Mail } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Cloudflare Turnstile site key (public key - safe to be in client code)
const TURNSTILE_SITE_KEY = "0x4AAAAAACL6QeIdlfk3uqvb";

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: {
        sitekey: string;
        callback: (token: string) => void;
        "expired-callback"?: () => void;
        "error-callback"?: () => void;
        theme?: "light" | "dark" | "auto";
        size?: "normal" | "compact";
      }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const FinalCTASection = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const { toast } = useToast();

  const benefits = t('finalCTA.benefits', { returnObjects: true }) as string[];

  // Load Turnstile script
  useEffect(() => {
    if (window.turnstile) {
      setTurnstileLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => setTurnstileLoaded(true);
    document.head.appendChild(script);

    return () => {
      // Cleanup widget on unmount
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, []);

  // Render Turnstile widget when script is loaded
  useEffect(() => {
    if (turnstileLoaded && turnstileRef.current && window.turnstile && !widgetIdRef.current) {
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => {
          setTurnstileToken(token);
        },
        "expired-callback": () => {
          setTurnstileToken(null);
        },
        "error-callback": () => {
          setTurnstileToken(null);
        },
        theme: "light",
        size: "normal",
      });
    }
  }, [turnstileLoaded]);

  const resetTurnstile = () => {
    setTurnstileToken(null);
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  const handleQuickDemo = async () => {
    if (!email) {
      toast({
        title: t('finalCTA.toast.emailRequired'),
        description: t('finalCTA.toast.emailRequiredDesc'),
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: t('finalCTA.toast.emailInvalid'),
        description: t('finalCTA.toast.emailInvalidDesc'),
        variant: "destructive",
      });
      return;
    }

    // Check CAPTCHA
    if (!turnstileToken) {
      toast({
        title: t('finalCTA.toast.captchaRequired', { defaultValue: "Verification required" }),
        description: t('finalCTA.toast.captchaRequiredDesc', { defaultValue: "Please complete the security verification" }),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('send-demo-request', {
        body: { email, turnstileToken }
      });

      if (error) throw error;

      toast({
        title: t('finalCTA.toast.success'),
        description: t('finalCTA.toast.successDesc'),
      });
      
      setEmail("");
      resetTurnstile();
    } catch (error) {
      console.error('Error sending demo request:', error);
      toast({
        title: t('finalCTA.toast.error'),
        description: t('finalCTA.toast.errorDesc'),
        variant: "destructive",
      });
      resetTurnstile();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="final-cta" className="py-24 px-4 bg-gradient-to-br from-primary via-primary to-primary/90 text-primary-foreground relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/5 rounded-full blur-3xl"></div>
      </div>
      
      <div className="container mx-auto relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-12">
          {/* Main heading */}
          <div className="space-y-6">
            <h2 className="text-4xl lg:text-6xl font-bold leading-tight">
              {t('finalCTA.title')}
            </h2>
            <p className="text-xl lg:text-2xl opacity-90 leading-relaxed max-w-3xl mx-auto">
              {t('finalCTA.subtitle')}
            </p>
          </div>

          {/* Benefits grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                <CheckCircle className="h-5 w-5 text-green-300 flex-shrink-0" />
                <span className="text-sm font-medium">{benefit}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="space-y-8">
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-6 shadow-xl group flex-1 bg-white text-primary hover:bg-white/90" 
                asChild
              >
                <Link to="/company-registration">
                  <Users className="mr-2 h-5 w-5" />
                  {t('finalCTA.createAccount')}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>

            {/* Quick demo form */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-2xl mx-auto">
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Calendar className="h-5 w-5" />
                  <span className="font-semibold">{t('finalCTA.demoForm.title')}</span>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Input
                      type="email"
                      placeholder={t('finalCTA.demoForm.placeholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-white/90 border-white/20 text-foreground placeholder:text-muted-foreground h-12"
                    />
                  </div>
                  <Button 
                    onClick={handleQuickDemo}
                    size="lg"
                    variant="secondary"
                    className="bg-white text-primary hover:bg-white/90 px-6 h-12"
                    disabled={isLoading || !turnstileToken}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    {isLoading ? t('finalCTA.demoForm.sending') : t('finalCTA.demoForm.button')}
                  </Button>
                </div>
                
                {/* Turnstile CAPTCHA widget */}
                <div className="flex justify-center mt-4">
                  <div ref={turnstileRef} />
                </div>
                
                <p className="text-sm opacity-75 text-center">
                  {t('finalCTA.demoForm.subtitle')}
                </p>
              </div>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-sm opacity-75">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>{t('finalCTA.trustIndicators.gdpr')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>{t('finalCTA.trustIndicators.support')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                <span>{t('finalCTA.trustIndicators.integration')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTASection;
